/**
 * components/print/SuratJalanPusat.js
 * Surat Jalan Pusat (PRD §5.4) — describes PACKAGES, not contents: no item list at all, just
 * `packing.suratJalanRows` (quantity + free-text description per row), captured by
 * `SuratJalanDialog.js` and saved via the unchanged `savePacking` write path.
 *
 * Portal-based, same pattern `InvoicePusat.js` established (see that file's own header comment
 * for the full story) — `createPortal(..., document.body)` so `#root` can be hidden with a
 * real `display:none` in print, which is what makes the named `@page` size honored at all.
 * Every future print document MUST follow this same pattern.
 *
 * **No letterhead** — PRD §5.4 doesn't list one (unlike §5.2 Invoice Pusat and §5.6 Checklist,
 * which both explicitly say "Letterhead"), and the client's own real Surat Jalan Pusat sample
 * shows none above `SURAT JALAN : ...` either. No `company` prop — nothing here would read it.
 *
 * **Phase T2c-2-fix**: this document was originally built on `.print-doc--pusat` (the full
 * 9.5in×11in continuous form) under a wrong "pusat documents = full page" generalisation —
 * confirmed wrong via real client screenshots showing a large blank gap below the table even
 * with zero rows missing from the fill. This is actually a SMALL document — a single package
 * manifest, not a multi-line invoice — so it now renders on `.print-doc--cabang` (the SAME
 * short 9.5in×5.5in page the future cabang documents will use; see printFoundation.css's own
 * header comment for the geometry facts this activation surfaced). `ROWS_PER_PAGE_SURAT_JALAN`
 * was retuned from a guessed `20` to a real value measured against the corrected page's content
 * height. The footer also lost its bordered box — see `.print-doc__footer--plain` below.
 *
 * **Address overrides for printing**: a screen-only editor sits to the LEFT of the document,
 * side by side (`.print-doc-overlay__body` — see InvoicePusat.js's own header comment for the
 * full layout story, incl. the narrow-viewport stacked fallback). Lets staff correct the buyer
 * address for THIS print only, defaulting from the
 * FROZEN `txn.buyerBlock.address` (never a live Contact lookup — see InvoicePusat.js's own
 * header comment for the full reasoning) and persisting to
 * `txn.addressOverrides.suratJalanPusat` only on Cetak. A blank field blocks Cetak.
 *
 * **The cabang @page geometry fix**: `.print-doc--cabang` (below) now sits inside a page
 * declared at the SAME 8.5in×11in geometry as `.print-doc--pusat` — a real physical print of
 * this exact document overflowed onto a second sheet on the real EPSON LX-300+II, traced to the
 * driver picking the wrong paper preset for a landscape-shaped `8.5in×5.5in` page request. This
 * document's OWN layout/rows/`ROWS_PER_PAGE_SURAT_JALAN` are UNCHANGED — see
 * `printFoundation.css`'s own header comment (last dated paragraph) for the full investigation.
 */
import React, { useState } from "react";
import { createPortal } from "react-dom";
import "./printFoundation.css";
import { paginateLines, NBSP, PRINT_SETTINGS_REMINDER_CABANG } from "../../utils/pagination";
import { fmtDateLong } from "../../utils/idGenerators";
import AddressOverrideField from "./AddressOverrideField";

// Re-measured in Phase T2c-2-fix-2 (was 9): T2c-2-fix's `9` budgeted the footer's own height but
// NOT the fixed gap now separating it from the table (`.print-doc__footer--plain`'s `margin-top:
// 20px`, replacing `margin-top: auto` — see printFoundation.css's own comment for why anchoring
// to the bottom was wrong for this document and pushed the footer onto a real second sheet).
// Real headless-Chrome print-media render against the corrected 5.0in content height: 422.4px
// inner content height − 104px header/buyer-block overhead − 26.98px thead row − 20px fixed gap
// − 38px footer, leaving 233.4px for data rows at a real measured row height of 26.98px →
// floor(233.4 / 26.98) = 8. Confirmed via page.pdf(): 8 rows (plus the gap and footer) render on
// one real physical page with no overflow; 9 rows genuinely split onto a second page.
//
// **Print-width foundational correction — re-measured, LOWERED from `8` to `7`.** The
// Keterangan column shrank from 6.9in to 6.4in once `.print-doc--cabang`'s content width was
// corrected 8.7in→8.2in (see printFoundation.css's own header comment for the physical
// discovery). At the new narrower column, a genuinely long real Keterangan description (a full
// sentence, ~85 characters) now wraps to 2 lines where it previously fit on 1 — a real `page.pdf()`
// re-measurement of 8 rows with one such wrapped description overflows by a measured 3.4px
// (page box grows to 483.4375px against the fixed 480px limit); a moderate ~50-character
// description still fits on one line and shows no regression at all. `7` restores a real,
// confirmed non-negative margin at the same worst case — re-verify via `page.pdf()` before
// changing this value again.
const ROWS_PER_PAGE_SURAT_JALAN = 7;

export default function SuratJalanPusat({ txn, onClose, onSaveAddressOverride }) {
  const buyer = txn.buyerBlock || { name: "", address: "" };
  const savedOverride = txn.addressOverrides?.suratJalanPusat;
  const [buyerAddressOverride, setBuyerAddressOverride] = useState(
    () => savedOverride?.buyerAddress ?? (txn.buyerBlock?.address || "")
  );
  const canPrint = buyerAddressOverride.trim() !== "";
  const handleCetak = () => {
    if (!canPrint) return;
    onSaveAddressOverride?.(txn.id, "suratJalanPusat", { buyerAddress: buyerAddressOverride.trim() });
    window.print();
  };
  const rows = txn.packing?.suratJalanRows || [];
  // `paginateLines` sums `qty*unitPrice` internally (Invoice Pusat's own running-subtotal
  // need) — this document has no money at all, so `qty:1`/`unitPrice:0` are dummy values that
  // satisfy that internal call without meaning anything. Deliberately NOT real numbers: if
  // this document ever grew a reason to read `page.carryIn`/`page.pageTotal`, non-dummy values
  // would let a wrong total slip through unnoticed (a surat jalan must never imply a price).
  // Only `page.lines`/`pageCount` are read below — the money fields are discarded by design.
  const { pages } = paginateLines(
    rows.map((r) => ({ ...r, qty: 1, unitPrice: 0 })),
    ROWS_PER_PAGE_SURAT_JALAN
  );

  return createPortal(
    <div className="print-doc-overlay">
      <div className="print-doc-overlay__actions">
        <span className="print-doc-overlay__note">Surat Jalan Pusat — {txn.invoiceNumber || txn.id}</span>
        <span className="print-doc-overlay__reminder">{PRINT_SETTINGS_REMINDER_CABANG}</span>
        <button type="button" className="btn" onClick={onClose}>Tutup</button>
        <button type="button" className="btn btn-primary" onClick={handleCetak} disabled={!canPrint}>Cetak</button>
      </div>

      <div className="print-doc-overlay__body">
        <div className="print-doc-overlay__override-panel">
          <AddressOverrideField label="Alamat Pembeli" value={buyerAddressOverride} onChange={setBuyerAddressOverride} />
        </div>

        <div className="print-doc print-doc--cabang">
        {pages.map((page) => {
          const fillerCount = Math.max(0, ROWS_PER_PAGE_SURAT_JALAN - page.lines.length);
          return (
            <div className="print-page" key={page.pageNumber}>
              <div className="print-doc__header">
                <div className="print-doc__header-col">
                  <span className="print-doc__label">Surat Jalan : </span>{txn.invoiceNumber}
                </div>
                <div className="print-doc__header-col print-doc__header-col--right">
                  Tangerang, {fmtDateLong(txn.date)}
                </div>
              </div>

              <div style={{ marginTop: 10, marginBottom: 10 }}>
                <div className="print-doc__label">Pembeli :</div>
                <div>{buyer.name}</div>
                {buyerAddressOverride.split("\n").map((line, i) => (
                  <div key={i}>{line}</div>
                ))}
              </div>

              <div className="print-doc__table-area">
                <table className="print-doc__table">
                  <colgroup>
                    <col style={{ width: "1in" }} />
                    <col />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Quantity</th>
                      <th>Keterangan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {page.lines.map((r, i) => (
                      <tr key={i}>
                        <td>{r.quantity}</td>
                        <td className="wrap">{r.description}</td>
                      </tr>
                    ))}
                    {Array.from({ length: fillerCount }).map((_, i) => (
                      <tr className="print-doc__filler" key={`filler-${i}`}>
                        <td>{NBSP}</td><td>{NBSP}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="print-doc__footer print-doc__footer--plain">
                <div className="print-doc__footer-cell">
                  <div className="print-doc__footer-label">Tanda Terima,</div>
                  <div className="print-doc__footer-line" />
                </div>
                <div className="print-doc__footer-cell print-doc__footer-cell--center" />
                <div className="print-doc__footer-cell" style={{ alignItems: "flex-end", textAlign: "right" }}>
                  <div className="print-doc__footer-label">Hormat Kami,</div>
                  <div className="print-doc__footer-line" />
                </div>
              </div>
            </div>
          );
        })}
        </div>
      </div>
    </div>,
    document.body
  );
}
